import { Fragment } from 'preact'
import { Plus } from 'lucide-preact'
import type { BrandTokenSetViewDto, BrandTypeGroupViewDto, BrandTypeStyleViewDto } from '@/types/knowledgeBase'
import { Select } from '@/components/common/Select'
import { TextBox } from '@/components/common/TextBox'
import {
  firstFamilyName,
  isReserved,
  openerOf,
  ownTypeGroups,
  specimenText,
  styleCaption,
  styleExtras,
  systemFallback,
  tokenNoun,
  type TokenEditor,
} from '@/components/console/knowledgeBase/brandGuidelinesModel'
import type { FontLoad } from '@/components/console/knowledgeBase/useBrandFonts'
import { AddButton, InlineForm, Refusal, RemoveQuestion, RolePills, RowActions } from './BrandEditControls'
import { EYEBROW, H2, LEDE, MONO, SECONDARY_BUTTON, SECTION, SWATCH_FRAME } from './brandGuidelinesStyles'
import { roleNote, type Pointers, type TokenEditing } from './brandEditing'

interface Props {
  set: BrandTokenSetViewDto | null
  fonts: Record<string, FontLoad>
  pointers: Pointers
  canChange: boolean
  pending: boolean
  edit: TokenEditing
  locked: boolean
}

/** A style's own fields. A change leaves the style in its group, with its letter spacing and case as they were. */
const STYLE_FIELDS = ['Name', 'FontSize', 'LineHeight', 'FontWeight']
/** The first style of a new group also names the group and the font family it is set in. */
const NEW_GROUP_FIELDS = ['Group', 'Family', ...STYLE_FIELDS]
const FAMILY_FIELDS = ['Key', 'Stack']

/** A face as the page draws it: the stack as given, or its fallback when the first face could not load. */
interface Face {
  family: string | null
  failed: boolean
  fontFamily: string | undefined
}

/** A font family as a list names it, by its key and the face it starts with: heading · Urbanist. */
function familyLabel(key: string, stack: string): string {
  const face = firstFamilyName(stack)
  return face ? `${key} · ${face}` : key
}

/**
 * The brand's font families and type styles, each style drawn in its own face on the brand's ground (ORB-904). On the
 * company's own page every family and style is added, changed and removed in place (ORB-973), one at a time. The
 * OrbitalX group holds the Heading and Body roles, which change under What OrbitalX uses, so it is not listed.
 */
export function BrandTypeSection({ set, fonts, pointers, canChange, pending, edit, locked }: Props) {
  const families = Object.entries(set?.Type?.Families ?? {})
  const ownGroups = ownTypeGroups(set?.Type?.Groups)
  // A populate can leave a group whose styles the grammar all dropped; the company's own page shows it to add a style to.
  const groups = canChange ? ownGroups : ownGroups.filter(group => group.Styles.length > 0)
  const styleCount = ownGroups.reduce((total, group) => total + group.Styles.length, 0)
  const ground = set?.Roles.Background.Hex ?? '#FFFFFF'
  const ink = set?.Roles.Text.Hex ?? '#0F172A'
  const styleEditor = edit.editor?.kind === 'Style' ? edit.editor : null
  const familyEditor = edit.editor?.kind === 'Family' ? edit.editor : null
  const newGroup = styleEditor !== null && styleEditor.current === null && styleEditor.slot === null

  const faceOf = (stack: string | null | undefined): Face => {
    const family = firstFamilyName(stack)
    const failed = family !== null && fonts[family] === 'failed'
    return { family, failed, fontFamily: failed ? systemFallback(stack) : stack ?? undefined }
  }
  const stackOf = (key: string) => set?.Type?.Families[key] ?? null
  // A style that stands for another is drawn as the style its chain of aliases ends at, in the face of that one's group.
  const byName = new Map(ownGroups.flatMap(group => group.Styles.map(style => [style.Name, { style, group }] as const)))
  const aliasTarget = (style: BrandTypeStyleViewDto) => {
    const seen = new Set([style.Name])
    let target = style.Alias ? byName.get(style.Alias) ?? null : null
    while (target?.style.Alias && !seen.has(target.style.Name)) {
      seen.add(target.style.Name)
      target = byName.get(target.style.Alias) ?? null
    }
    return target
  }

  const blankStyle = { Name: '', FontSize: '', LineHeight: '', FontWeight: '' }
  const changeStyle = (style: BrandTypeStyleViewDto, group: BrandTypeGroupViewDto) => edit.open('Style', style.Name, group.Name, {
    Name: style.Name, FontSize: style.FontSize ?? '', LineHeight: style.LineHeight ?? '', FontWeight: style.FontWeight ?? '',
  })
  const addStyle = (group: BrandTypeGroupViewDto) => edit.open('Style', null, group.Name, blankStyle)
  const addGroup = () => edit.open('Style', null, null, { Group: '', Family: families[0]?.[0] ?? '', ...blankStyle })

  const styleForm = (face: Face, stored?: BrandTypeStyleViewDto, heading?: string) => styleEditor && (
    <StyleForm editor={styleEditor} heading={heading} families={families} face={face} stored={stored} ground={ground} ink={ink}
      pending={pending} note={styleEditor.current === null ? null : roleNote(pointers.get(styleEditor.current), tokenNoun('Style'))}
      onField={edit.setField} onCancel={edit.close} onSave={() => void edit.save(newGroup ? NEW_GROUP_FIELDS : STYLE_FIELDS)} />
  )
  const familyForm = () => familyEditor && (
    <FamilyForm editor={familyEditor} pending={pending} onField={edit.setField} onCancel={edit.close}
      onSave={() => void edit.save(FAMILY_FIELDS)} />
  )

  /** A run of a group's styles on one ground, as one page; a style's open form splits the group into two runs. */
  const specimens = (styles: BrandTypeStyleViewDto[], group: BrandTypeGroupViewDto, face: Face) => styles.length > 0 && (
    <div className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] sm:grid-cols-[200px_minmax(0,1fr)] gap-x-6">
      <div aria-hidden="true" className={`col-start-2 ${SWATCH_FRAME}`}
        style={{ gridRow: `1 / ${styles.length + 1}`, background: ground }} />
      {styles.map((style, index) => {
        const target = aliasTarget(style)
        const shown = target?.style ?? style
        const shownFace = target ? faceOf(stackOf(target.group.Family)) : face
        const ref = { kind: 'Style' as const, name: style.Name, slot: group.Name }
        const refusal = edit.refusalOf('Style', style.Name)
        return (
          <Fragment key={style.Name}>
            <div className="col-start-1 pt-5 pb-4 flex flex-col gap-0.5" style={{ gridRow: index + 1 }}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`${MONO} font-semibold text-slate-900 dark:text-white`}>{style.Name}</span>
                <RolePills name={style.Name} pointers={pointers} />
              </div>
              {style.Alias ? (
                <span className={`${MONO} text-slate-500 dark:text-slate-400`}>
                  <span className="sr-only">Points at </span><span aria-hidden="true">&#8594; </span>{style.Alias}
                </span>
              ) : (
                <>
                  <span className="text-sm text-slate-600 dark:text-slate-300">{styleCaption(face.family, style)}</span>
                  {styleExtras(style) && <span className="text-sm text-slate-600 dark:text-slate-300">{styleExtras(style)}</span>}
                </>
              )}
              {shownFace.failed && <span className="text-xs text-slate-500 dark:text-slate-400">Shown in a system font</span>}
              {canChange && !isReserved(style.Name) && (
                <div className="pt-1.5">
                  {edit.isAsking('Style', style.Name)
                    ? <RemoveQuestion kind="Style" name={style.Name} pending={pending} onRemove={() => void edit.remove(ref)} onKeep={edit.keep} />
                    : (
                      // A style that stands for another has no sizes of its own to change.
                      <span className="-ml-1.5">
                        <RowActions kind="Style" name={style.Name} pending={pending || locked}
                          onChange={style.Alias ? undefined : () => changeStyle(style, group)} onRemove={() => edit.ask(ref)} />
                      </span>
                    )}
                </div>
              )}
              {refusal && <div className="pt-1"><Refusal message={refusal} /></div>}
            </div>
            <p className="col-start-2 relative px-7 py-5 min-w-0 break-words" style={{
              gridRow: index + 1,
              color: ink,
              borderTop: index > 0 ? `1px solid color-mix(in srgb, ${ink} 12%, transparent)` : undefined,
              fontFamily: shownFace.fontFamily,
              fontSize: shown.FontSize ?? undefined,
              lineHeight: shown.LineHeight ?? undefined,
              fontWeight: shown.FontWeight ?? undefined,
              letterSpacing: shown.LetterSpacing ?? undefined,
              textTransform: shown.TextTransform ?? undefined,
            }}>
              {specimenText(shown)}
            </p>
          </Fragment>
        )
      })}
    </div>
  )

  // A removed or renamed font family can come back with Populate, so only styles are promised; a family's fonts are kept.
  const lede = styleCount > 0
    ? `${families.length} ${families.length === 1 ? 'family' : 'families'} and ${styleCount} ${styleCount === 1 ? 'style' : 'styles'}.${
      canChange ? ' Styles you add, change or remove here stay that way when Populate from website runs.' : ''}`
    : !canChange
      ? 'No type styles yet.'
      : families.length === 0
        ? 'No type styles yet. Populate from website fills this, or add a font family here and then a group of styles set in it.'
        : 'No type styles yet. Populate from website fills this, or add a group of styles here.'

  return (
    <section aria-labelledby="bg-type-h" className={SECTION}>
      <div className="flex flex-col gap-1.5">
        <h2 id="bg-type-h" className={H2}>Type</h2>
        <p className={LEDE}>{lede}</p>
      </div>
      {(families.length > 0 || groups.length > 0 || canChange) && (
        <div className="flex flex-col gap-11">
          {(families.length > 0 || canChange) && (
            <div className="flex flex-col gap-3">
              {families.length > 0 && (
                <ul aria-label="Font families" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {families.map(([key, stack]) => {
                    if (familyEditor?.current === key) return <li key={key} className="md:col-span-2">{familyForm()}</li>
                    const face = faceOf(stack)
                    const ref = { kind: 'Family' as const, name: key, slot: null }
                    const refusal = edit.refusalOf('Family', key)
                    return (
                      <li key={key} className="min-w-0 flex items-center gap-4">
                        <span aria-hidden="true" className={`w-14 h-14 flex-none grid place-items-center text-2xl leading-none ${SWATCH_FRAME}`}
                          style={{ background: ground, color: ink, fontFamily: face.fontFamily }}>
                          Aa
                        </span>
                        <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                          <span className={`${MONO} font-semibold text-slate-900 dark:text-white`}>{key}</span>
                          <span className="font-mono text-xs text-slate-600 dark:text-slate-300 truncate">{stack}</span>
                          {face.failed && <span className="text-xs text-slate-500 dark:text-slate-400">Shown in a system font</span>}
                          {refusal && <Refusal message={refusal} />}
                        </span>
                        {canChange && (edit.isAsking('Family', key)
                          ? <RemoveQuestion kind="Family" name={key} pending={pending} className="flex-none max-w-[55%] justify-end"
                            onRemove={() => void edit.remove(ref)} onKeep={edit.keep} />
                          : (
                            <span className="flex-none">
                              <RowActions kind="Family" name={key} pending={pending || locked}
                                onChange={() => edit.open('Family', key, null, { Key: key, Stack: stack })} onRemove={() => edit.ask(ref)} />
                            </span>
                          ))}
                      </li>
                    )
                  })}
                </ul>
              )}
              {canChange && (familyEditor !== null && familyEditor.current === null
                ? familyForm()
                : <AddButton label="Add a font family" kind="Family" slot={null} pending={pending || locked}
                  onClick={() => edit.open('Family', null, null, { Key: '', Stack: '' })} />)}
            </div>
          )}
          {groups.map(group => {
            const face = faceOf(stackOf(group.Family))
            const editing = styleEditor?.current ?? null
            const editedIndex = editing === null ? -1 : group.Styles.findIndex(style => style.Name === editing)
            const addingHere = styleEditor !== null && styleEditor.current === null && styleEditor.slot === group.Name
            return (
              <div key={group.Name} className="flex flex-col gap-3.5">
                <h3 className={EYEBROW}>{group.Name}</h3>
                {editedIndex < 0 ? specimens(group.Styles, group, face) : (
                  <>
                    {specimens(group.Styles.slice(0, editedIndex), group, face)}
                    {styleForm(face, group.Styles[editedIndex])}
                    {specimens(group.Styles.slice(editedIndex + 1), group, face)}
                  </>
                )}
                {canChange && (addingHere ? styleForm(face) : (
                  <AddButton label="Add style" ariaLabel={`Add a style to ${group.Name}`} kind="Style" slot={group.Name}
                    pending={pending || locked} onClick={() => addStyle(group)} />
                ))}
              </div>
            )
          })}
        </div>
      )}
      {canChange && (newGroup && styleEditor !== null
        ? styleForm(faceOf(stackOf(styleEditor.fields.Family)), undefined, 'New group')
        : (
          <div className="flex flex-col items-start gap-2">
            <button type="button" className={SECONDARY_BUTTON} disabled={pending || locked || families.length === 0} aria-label="Add a group of styles"
              aria-describedby={families.length === 0 ? 'bg-type-group-wait' : undefined} data-opener={openerOf('add', 'Style', null)}
              onClick={addGroup}>
              <Plus className="w-4 h-4" aria-hidden="true" />Add group
            </button>
            {families.length === 0 && (
              <p id="bg-type-group-wait" className="text-xs text-slate-500 dark:text-slate-400">
                Add a font family first: each group of styles is set in one.
              </p>
            )}
          </div>
        ))}
    </section>
  )
}

function StyleForm({ editor, heading, families, face, stored, ground, ink, pending, note, onField, onCancel, onSave }: {
  editor: TokenEditor
  heading?: string
  families: [string, string][]
  face: Face
  /** The style as stored, whose letter spacing and case a change keeps, so the sample shows it as it will be. */
  stored?: BrandTypeStyleViewDto
  ground: string
  ink: string
  pending: boolean
  note: string | null
  onField: (key: string, value: string) => void
  onCancel: () => void
  onSave: () => void
}) {
  const { fields } = editor
  const sample = { FontSize: fields.FontSize.trim() || null, TextTransform: stored?.TextTransform ?? null }
  return (
    <InlineForm editor={editor} heading={heading} pending={pending} note={note} onCancel={onCancel} onSave={onSave}>
      {editor.current === null && editor.slot === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextBox name="bg-style-group" label="Group" density="compact" value={fields.Group} disabled={pending}
            placeholder="Headings" error={editor.errors.Group} onChange={value => onField('Group', value)} />
          <Select name="bg-style-family" label="Font family" density="compact" value={fields.Family} disabled={pending}
            placeholder="Choose a font family" options={families.map(([key, stack]) => ({ value: key, label: familyLabel(key, stack) }))}
            error={editor.errors.Family} onChange={(event: Event) => onField('Family', (event.target as HTMLSelectElement).value)} />
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <TextBox name="bg-style-name" label="Name" density="compact" value={fields.Name} disabled={pending}
          placeholder="body-lg" error={editor.errors.Name} onChange={value => onField('Name', value)} />
        <TextBox name="bg-style-size" label="Size" density="compact" value={fields.FontSize} disabled={pending}
          placeholder="18px" error={editor.errors.FontSize} onChange={value => onField('FontSize', value)} />
        <TextBox name="bg-style-line-height" label="Line height" density="compact" value={fields.LineHeight} disabled={pending}
          placeholder="26px" error={editor.errors.LineHeight} onChange={value => onField('LineHeight', value)} />
        <TextBox name="bg-style-weight" label="Weight" density="compact" value={fields.FontWeight} disabled={pending}
          placeholder="400" error={editor.errors.FontWeight} onChange={value => onField('FontWeight', value)} />
      </div>
      <p aria-hidden="true" className={`px-6 py-4 min-w-0 break-words ${SWATCH_FRAME}`} style={{
        background: ground,
        color: ink,
        fontFamily: face.fontFamily,
        fontSize: sample.FontSize ?? undefined,
        lineHeight: fields.LineHeight.trim() || undefined,
        fontWeight: fields.FontWeight.trim() || undefined,
        letterSpacing: stored?.LetterSpacing ?? undefined,
        textTransform: sample.TextTransform ?? undefined,
      }}>
        {specimenText(sample)}
      </p>
    </InlineForm>
  )
}

function FamilyForm({ editor, pending, onField, onCancel, onSave }: {
  editor: TokenEditor
  pending: boolean
  onField: (key: string, value: string) => void
  onCancel: () => void
  onSave: () => void
}) {
  return (
    <InlineForm editor={editor} pending={pending} onCancel={onCancel} onSave={onSave}>
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4">
        <TextBox name="bg-family-key" label="Key" density="compact" value={editor.fields.Key} disabled={pending}
          placeholder="heading" error={editor.errors.Key}
          helperText={editor.current === null ? 'A short name its groups use, such as heading or body.' : undefined}
          onChange={value => onField('Key', value)} />
        <TextBox name="bg-family-stack" label="Fonts" density="compact" value={editor.fields.Stack} disabled={pending}
          placeholder={'"Inter", sans-serif'} error={editor.errors.Stack} onChange={value => onField('Stack', value)} />
      </div>
    </InlineForm>
  )
}
